import { ClearBaseIcon } from '@tableau/icons/ClearBaseIcon';
import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ModelSettingsSectionStyles } from '~src/settings/general-settings/styles/tableau-gai/ModelSettingsSectionStyles';
import { TableauGaiSharedStyles } from '~src/settings/general-settings/styles/tableau-gai/TableauGaiSharedStyles';

import { Messages } from '../../../../../codegen/Localize';
import { openErrorDetailsModal } from './ErrorDetailsModal';
import { ModelTestResult } from './GenerativeAIOnPremSection';
import { DEFAULT_MODELS } from './GenerativeAITypes';

function formatModelDisplayName(modelName: string, defaultModel: string, isOpenAIProvider: boolean): string {
  if (!modelName) {
    return Messages.gaiModelSettingsModelNotSpecified();
  }
  if (FeatureFlags.defaultModelSelectionEnabled && isOpenAIProvider && modelName === defaultModel) {
    return `${Messages.selectModelsDialogAutomatic()} (${modelName})`;
  }
  return modelName;
}

function OnlineModelsDisplay(props: {
  isConnectionValid: boolean;
  queryParsingModel: string;
  vizAndCalcGenerationModel: string;
  embeddingsModel: string;
  isDefaultOnlineModels: boolean;
}): React.ReactElement {
  if (!props.isConnectionValid) {
    return <p>{Messages.gaiModelSettingsSalesforceConnectionRequired()}</p>;
  }

  if (props.isDefaultOnlineModels) {
    return <p>{Messages.gaiModelSettingsDefaultModels()}</p>;
  }

  return (
    <ul className={ModelSettingsSectionStyles.modelsList}>
      <li>
        {Messages.gaiModelSettingsModelLabelWithColon({ MODEL_LABEL: Messages.gaiModelSettingsQueryParsingModelLabel() })}{' '}
        {formatModelDisplayName(props.queryParsingModel, DEFAULT_MODELS.QUERY_PARSING, false)}
      </li>
      <li>
        {Messages.gaiModelSettingsModelLabelWithColon({ MODEL_LABEL: Messages.gaiModelSettingsVizAndCalcGenerationModelLabel() })}{' '}
        {formatModelDisplayName(props.vizAndCalcGenerationModel, DEFAULT_MODELS.VIZ_AND_CALC, false)}
      </li>
      <li>
        {Messages.gaiModelSettingsModelLabelWithColon({ MODEL_LABEL: Messages.gaiModelSettingsEmbeddingsModelLabel() })}{' '}
        {formatModelDisplayName(props.embeddingsModel, DEFAULT_MODELS.EMBEDDINGS, false)}
      </li>
    </ul>
  );
}

function OnPremModelsDisplay(props: {
  isConnectionValid: boolean;
  isOpenAIProvider: boolean;
  queryParsingModel: string;
  vizAndCalcGenerationModel: string;
  embeddingsModel: string;
}): React.ReactElement {
  return (
    <ul className={props.isConnectionValid ? ModelSettingsSectionStyles.modelsList : ModelSettingsSectionStyles.modelsListDisabled}>
      <li>
        {Messages.gaiModelSettingsModelLabelWithColon({ MODEL_LABEL: Messages.gaiModelSettingsQueryParsingModelLabel() })}{' '}
        {formatModelDisplayName(props.queryParsingModel, DEFAULT_MODELS.QUERY_PARSING, props.isOpenAIProvider)}
      </li>
      <li>
        {Messages.gaiModelSettingsModelLabelWithColon({ MODEL_LABEL: Messages.gaiModelSettingsVizAndCalcGenerationModelLabel() })}{' '}
        {formatModelDisplayName(props.vizAndCalcGenerationModel, DEFAULT_MODELS.VIZ_AND_CALC, props.isOpenAIProvider)}
      </li>
      <li>
        {Messages.gaiModelSettingsModelLabelWithColon({ MODEL_LABEL: Messages.gaiModelSettingsEmbeddingsModelLabel() })}{' '}
        {formatModelDisplayName(props.embeddingsModel, DEFAULT_MODELS.EMBEDDINGS, props.isOpenAIProvider)}
      </li>
    </ul>
  );
}

export function ModelSettingsSection(props: {
  isOnlineEnvironment: boolean;
  isConnectionValid: boolean;
  isOpenAIProvider: boolean;
  isCustomGateway?: boolean;
  openSelectModelsDialog: () => void;
  showEditModelsError: boolean;
  dismissEditModelsError: () => void;
  queryParsingModel: string;
  vizAndCalcGenerationModel: string;
  embeddingsModel: string;
  isDefaultOnlineModels: boolean;
  // Optional — only the on-prem section uses these today. Cloud/test callers
  // can omit them to keep the legacy showEditModelsError-only render path.
  modelTestResults?: ModelTestResult[] | null;
  hasUnavailableSelectedModels?: boolean;
}): React.ReactElement {
  const modelTestResults = props.modelTestResults ?? null;
  const showModelTestAlerts = FeatureFlags.testSelectedModelsOnTest && modelTestResults !== null;
  const isEditEnabled = props.isConnectionValid || !!props.isCustomGateway;

  // Let the admin dismiss the "Model settings valid" confirmation. Reset the
  // dismissal whenever a new test runs so the fresh result is shown again.
  const [successDismissed, setSuccessDismissed] = React.useState<boolean>(false);
  React.useEffect(() => {
    setSuccessDismissed(false);
  }, [modelTestResults]);
  return (
    <>
      <p className={ModelSettingsSectionStyles.sectionLabel}>
        <b>{Messages.gaiModelSettingsModelSettingsLabel()}</b>
      </p>
      <p>{Messages.gaiModelSettingsModelSettingsDescription()}</p>

      <div>
        <ButtonWidget
          buttonType={ButtonType.Outline}
          testId="edit-models-button"
          onClick={props.openSelectModelsDialog}
          disabled={!isEditEnabled}
        >
          {Messages.gaiModelSettingsEditModelsLabel()}
        </ButtonWidget>
      </div>

      <div className={ModelSettingsSectionStyles.modelsContainer}>
        {showModelTestAlerts
          ? renderModelTestAlerts(modelTestResults!, successDismissed, () => setSuccessDismissed(true))
          : props.showEditModelsError && (
            <div className={ModelSettingsSectionStyles.alertContainer}>
              <CompactInlineAlertWidget
                testId={'edit-models-error'}
                type={InlineAlertType.Error}
                icon={<ClearBaseIcon size={14} />}
                onClick={props.dismissEditModelsError}
              >
                {Messages.gaiModelSettingsEditModelsConnectionFailedMessage()}
              </CompactInlineAlertWidget>
            </div>
          )}
        {props.hasUnavailableSelectedModels && (
          <div className={ModelSettingsSectionStyles.alertContainer}>
            <CompactInlineAlertWidget
              testId={'selected-models-unavailable-warning'}
              type={InlineAlertType.Error}
            >
              {Messages.gaiModelSettingsSelectedModelsUnavailableWarning()}
            </CompactInlineAlertWidget>
          </div>
        )}
        {props.isOnlineEnvironment ? (
          <OnlineModelsDisplay
            isConnectionValid={props.isConnectionValid}
            queryParsingModel={props.queryParsingModel}
            vizAndCalcGenerationModel={props.vizAndCalcGenerationModel}
            embeddingsModel={props.embeddingsModel}
            isDefaultOnlineModels={props.isDefaultOnlineModels}
          />
        ) : (
          <OnPremModelsDisplay
            isConnectionValid={isEditEnabled}
            isOpenAIProvider={props.isOpenAIProvider}
            queryParsingModel={props.queryParsingModel}
            vizAndCalcGenerationModel={props.vizAndCalcGenerationModel}
            embeddingsModel={props.embeddingsModel}
          />
        )}
      </div>
    </>
  );
}

function renderModelTestAlerts(results: ModelTestResult[], successDismissed: boolean, dismissSuccess: () => void): React.ReactNode {
  if (results.length === 0) {
    return null;
  }

  const failures = results.filter((result) => !result.success);

  // When every model passed, confirm the model settings are valid. Dismissible
  // like the top-level "LLM provider connected" alert.
  if (failures.length === 0) {
    if (successDismissed) {
      return null;
    }
    return (
      <div className={ModelSettingsSectionStyles.alertContainer}>
        <CompactInlineAlertWidget
          testId={'model-test-success'}
          type={InlineAlertType.Success}
          icon={<ClearBaseIcon size={14} />}
          onClick={dismissSuccess}
        >
          {Messages.gaiModelSettingsModelSettingsValid()}
        </CompactInlineAlertWidget>
      </div>
    );
  }

  return (
    <div className={ModelSettingsSectionStyles.alertContainer}>
      {failures.map((failure) => (
        <InlineAlertWidget
          key={failure.key}
          testId={`model-test-failure-${failure.key}`}
          type={InlineAlertType.Error}
          className={TableauGaiSharedStyles.alertButtonRightAligned}
          button={failure.errorDetail ? (
            <ButtonWidget
              buttonType={ButtonType.Outline}
              testId={`model-test-error-details-${failure.key}`}
              onClick={() => openErrorDetailsModal(failure.errorDetail!)}
            >
              {Messages.onPremGAIErrorDetailsButtonLabel()}
            </ButtonWidget>
          ) : undefined}
        >
          {Messages.onPremGAIModelTestFailed({ MODEL_LABEL: failure.modelLabel })}
        </InlineAlertWidget>
      ))}
    </div>
  );
}
